import { useState, useEffect } from 'react'
import { useGame } from '../../contexts/GameContext'
import { useAuth } from '../../contexts/AuthContext'
import soundManager from '../../utils/soundUtils'
import './GamePreferences.css'

const PIECE_THEMES = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional chess pieces',
    preview: '/images/pieces/classic/white-queen.png',
    premium: false
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    description: 'Futuristic neon-styled pieces',
    preview: '/images/pieces/cyberpunk/queenwhite.png',
    premium: true
  },
  {
    id: '2',
    name: 'Modern',
    description: 'Modern styled chess pieces',
    preview: '/images/pieces/2/whitequeen.png',
    premium: true
  },
  {
    id: '3',
    name: 'Artistic',
    description: 'Artistic styled chess pieces',
    preview: '/images/pieces/3/whitequeen.png',
    premium: true
  }
]

// Add state for sound testing
let lastSoundWasCapture = false

const BOARD_THEMES = [
  {
    id: 'riftchess',
    name: 'RiftChess',
    description: 'Signature dark grey board with gradient effects',
    lightSquare: '#2d2d2d',
    darkSquare: '#1a1a1a',
    premium: false
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional brown and beige board',
    lightSquare: '#f0d9b5',
    darkSquare: '#b58863',
    premium: false
  },
  {
    id: 'green',
    name: 'Green',
    description: 'Forest green and cream board',
    lightSquare: '#eeeed2',
    darkSquare: '#769656',
    premium: true
  },
  {
    id: 'blue',
    name: 'Blue',
    description: 'Ocean blue and white board',
    lightSquare: '#e8f4f8',
    darkSquare: '#4a90a4',
    premium: true
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'Cyberpunk-themed board',
    lightSquare: '#1a1a2e',
    darkSquare: '#0f3460',
    premium: true
  },
  {
    id: 'grey',
    name: 'Grey',
    description: 'Monochrome grey and white board',
    lightSquare: '#f5f5f5',
    darkSquare: '#999999',
    premium: true
  }
]

export default function GamePreferences() {
  const { updateThemes, getProfile, error } = useGame()
  const { session, isGuest, triggerRegistration, user } = useAuth()
  const [selectedPieceTheme, setSelectedPieceTheme] = useState('classic')
  const [selectedBoardTheme, setSelectedBoardTheme] = useState('riftchess')
  const [soundVolume, setSoundVolume] = useState(0.3)
  const [loading, setLoading] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)

  // Get user profile data for premium check
  const [userProfile, setUserProfile] = useState(null)

  // Check if user has premium access - check both user object and profile
  const isPremium = user?.account_type === 'premium' || userProfile?.account_type === 'premium'

  // Load preferences from profile API on component mount
  useEffect(() => {
    const loadThemes = async () => {
      if (!session?.access_token) {
        // Fallback to localStorage for non-authenticated users
        const savedPieceTheme = localStorage.getItem('riftchess-piece-theme')
        const savedBoardTheme = localStorage.getItem('riftchess-board-theme')
        const savedSoundVolume = localStorage.getItem('riftchess-sound-volume')
        if (savedPieceTheme) setSelectedPieceTheme(savedPieceTheme)
        if (savedBoardTheme) {
          // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
          const boardTheme = (savedBoardTheme === 'purple' || savedBoardTheme === 'darkgrey') ? 'riftchess' : savedBoardTheme
          setSelectedBoardTheme(boardTheme)
        }
        if (savedSoundVolume) {
          const volume = parseFloat(savedSoundVolume)
          setSoundVolume(volume)
          soundManager.setVolume(volume)
        }
        return
      }

      try {
        const profile = await getProfile()
        setUserProfile(profile) // Store profile data for premium check
        console.log('PREMIUM DEBUG: Profile loaded with account_type:', profile?.account_type)
        console.log('PREMIUM DEBUG: User object has account_type:', user?.account_type)

        if (profile.piece_theme) {
          setSelectedPieceTheme(profile.piece_theme)
        }
        if (profile.board_theme) {
          // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
          const boardTheme = (profile.board_theme === 'purple' || profile.board_theme === 'darkgrey') ? 'riftchess' : profile.board_theme
          console.log('THEME DEBUG: Loading board theme from profile:', profile.board_theme, '-> setting to:', boardTheme)
          setSelectedBoardTheme(boardTheme)
        }
      } catch (err) {
        console.error('Failed to load theme preferences:', err)
        // Fallback to localStorage on error
        const savedPieceTheme = localStorage.getItem('riftchess-piece-theme')
        const savedBoardTheme = localStorage.getItem('riftchess-board-theme')
        if (savedPieceTheme) setSelectedPieceTheme(savedPieceTheme)
        if (savedBoardTheme) {
          // Handle migration from 'purple' and 'darkgrey' to 'riftchess'
          const boardTheme = (savedBoardTheme === 'purple' || savedBoardTheme === 'darkgrey') ? 'riftchess' : savedBoardTheme
          setSelectedBoardTheme(boardTheme)
        }
      }
    }

    loadThemes()
  }, [session, getProfile, user])

  const handlePieceThemeChange = async (themeId) => {
    const theme = PIECE_THEMES.find(t => t.id === themeId)
    console.log('PREMIUM DEBUG: Piece theme change - isPremium:', isPremium, 'theme.premium:', theme?.premium)
    if (theme?.premium && !isPremium) {
      return // Don't allow selection of premium themes for non-premium users
    }

    setSelectedPieceTheme(themeId)

    if (!session?.access_token) {
      // Fallback to localStorage for non-authenticated users
      localStorage.setItem('riftchess-piece-theme', themeId)
      window.dispatchEvent(new CustomEvent('pieceThemeChanged', { detail: themeId }))
      return
    }

    try {
      setLoading(true)
      setSaveStatus(null)
      await updateThemes(themeId, selectedBoardTheme)
      setSaveStatus('saved')
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('pieceThemeChanged', { detail: themeId }))
    } catch (err) {
      console.error('Failed to save piece theme:', err)
      setSaveStatus('error')
      // Fallback to localStorage on error
      localStorage.setItem('riftchess-piece-theme', themeId)
    } finally {
      setLoading(false)
      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000)
    }
  }

  const handleBoardThemeChange = async (themeId) => {
    const theme = BOARD_THEMES.find(t => t.id === themeId)
    console.log('PREMIUM DEBUG: Board theme change - isPremium:', isPremium, 'theme.premium:', theme?.premium)
    if (theme?.premium && !isPremium) {
      return // Don't allow selection of premium themes for non-premium users
    }

    console.log('THEME DEBUG: Attempting to change board theme to:', themeId)
    setSelectedBoardTheme(themeId)

    if (!session?.access_token) {
      // Fallback to localStorage for non-authenticated users
      localStorage.setItem('riftchess-board-theme', themeId)
      window.dispatchEvent(new CustomEvent('boardThemeChanged', { detail: themeId }))
      return
    }

    try {
      setLoading(true)
      setSaveStatus(null)
      console.log('THEME DEBUG: Calling updateThemes with:', selectedPieceTheme, themeId)
      await updateThemes(selectedPieceTheme, themeId)
      console.log('THEME DEBUG: updateThemes succeeded')
      setSaveStatus('saved')
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('boardThemeChanged', { detail: themeId }))
    } catch (err) {
      console.error('THEME DEBUG: Failed to save board theme:', err)
      setSaveStatus('error')
      // Revert the selection on error
      console.log('THEME DEBUG: Reverting selection due to error')
      setSelectedBoardTheme(selectedBoardTheme)
      // Fallback to localStorage on error
      localStorage.setItem('riftchess-board-theme', themeId)
    } finally {
      setLoading(false)
      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000)
    }
  }

  const handleSoundVolumeChange = (volume) => {
    setSoundVolume(volume)
    soundManager.setVolume(volume)
    localStorage.setItem('riftchess-sound-volume', volume.toString())

    // Play alternating test sounds to give immediate feedback
    if (volume > 0) {
      // Alternate between move and capture sounds for testing
      soundManager.playMoveOrCapture(lastSoundWasCapture)
      lastSoundWasCapture = !lastSoundWasCapture
    }
  }

  if (isGuest) {
    return (
      <div className="game-preferences">
        <div className="guest-preferences-notice">
          <div className="guest-message">
            <h3>Create an Account</h3>
            <p>Sign up to save your theme preferences and access them from any device.</p>
            <div className="account-actions">
              <button onClick={triggerRegistration} className="create-account-button">
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="game-preferences">
      <div className="preferences-header">
        <h2>Game Preferences</h2>
        <p>Customize your chess board and piece appearance</p>
        {saveStatus === 'saved' && (
          <div className="save-status success">
            <span>✓ Preferences saved to your account</span>
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="save-status error">
            <span>⚠ Failed to save to account (saved locally)</span>
          </div>
        )}
        {loading && (
          <div className="save-status loading">
            <span>💾 Saving...</span>
          </div>
        )}
      </div>

      <div className="preferences-content">
        {/* Piece Theme Selection */}
        <section className="preference-section">
          <h3>Piece Theme</h3>
          <p className="section-description">Choose your preferred chess piece style</p>

          <div className="theme-grid">
            {PIECE_THEMES.map((theme) => {
              const isDisabled = theme.premium && !isPremium
              return (
                <div
                  key={theme.id}
                  className={`theme-card ${selectedPieceTheme === theme.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => handlePieceThemeChange(theme.id)}
                >
                  <div className="theme-preview piece-preview">
                    <img
                      src={theme.preview}
                      alt={`${theme.name} piece preview`}
                      onError={(e) => {
                        e.target.style.display = 'none'
                        e.target.nextElementSibling.style.display = 'flex'
                      }}
                    />
                    <div className="preview-fallback" style={{ display: 'none' }}>
                      <span className="fallback-icon">♔</span>
                    </div>
                  </div>
                  <div className="theme-info">
                    <h4>{theme.name}</h4>
                    <p>{isDisabled ? 'Premium Required' : theme.description}</p>
                  </div>
                  {theme.premium && (
                    <div className="preferences-premium-badge">
                      <span>PREMIUM</span>
                    </div>
                  )}
                  {selectedPieceTheme === theme.id && !isDisabled && (
                    <div className="selection-indicator">
                      <span>✓</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* Board Theme Selection */}
        <section className="preference-section">
          <h3>Board Theme</h3>
          <p className="section-description">Choose your preferred board color scheme</p>

          <div className="theme-grid">
            {BOARD_THEMES.map((theme) => {
              const isDisabled = theme.premium && !isPremium
              return (
                <div
                  key={theme.id}
                  className={`theme-card ${selectedBoardTheme === theme.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => handleBoardThemeChange(theme.id)}
                >
                  <div className="theme-preview board-preview">
                    <div className="mini-board">
                      {Array.from({ length: 4 }, (_, i) => (
                        <div
                          key={i}
                          className="mini-square"
                          style={{
                            backgroundColor: (Math.floor(i / 2) + i) % 2 === 0
                              ? theme.lightSquare
                              : theme.darkSquare
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="theme-info">
                    <h4>{theme.name}</h4>
                    <p>{isDisabled ? 'Premium Required' : theme.description}</p>
                  </div>
                  {theme.premium && (
                    <div className="preferences-premium-badge">
                      <span>PREMIUM</span>
                    </div>
                  )}
                  {selectedBoardTheme === theme.id && !isDisabled && (
                    <div className="selection-indicator">
                      <span>✓</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* Sound Settings */}
        <section className="preference-section">
          <h3>Sound Settings</h3>
          <p className="section-description">Adjust move sound volume</p>

          <div className="sound-controls">
            <div className="volume-control">
              <label htmlFor="volume-slider">Move Sound Volume: {Math.round(soundVolume * 100)}%</label>
              <input
                id="volume-slider"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={soundVolume}
                onChange={(e) => handleSoundVolumeChange(parseFloat(e.target.value))}
                className="volume-slider"
              />
              <div className="volume-labels">
                <span>🔇</span>
                <span>🔊</span>
              </div>
            </div>
          </div>
        </section>

        <div className="preferences-footer">
          <p className="footer-note">
            {session?.access_token
              ? 'Your preferences are saved to your account and sync across all devices.'
              : 'Your preferences are saved locally. Sign in to sync across devices.'
            }
          </p>
        </div>
      </div>
    </div>
  )
}